import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { HardDriveDownload, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function BackupsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canRun = hasRole(user, ['admin', 'tech']);
  const isAdmin = hasRole(user, ['admin']);
  const [items, setItems] = useState([]);
  const [schedule, setSchedule] = useState({ enabled: true, cron: '0 3 * * *', retention_days: 30 });

  const load = async () => {
    const [{ data: b }, { data: s }] = await Promise.all([api.get('/backups'), api.get('/backups/schedule')]);
    setItems(b);
    if (s && s.cron) setSchedule({ enabled: !!s.enabled, cron: s.cron, retention_days: s.retention_days ?? 30 });
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    try { await api.post('/backups/run'); toast.success(t('success')); load(); }
    catch (e) { toast.error(t('error')); }
  };
  const saveSchedule = async () => {
    try { await api.put('/backups/schedule', schedule); toast.success(t('success')); load(); }
    catch (e) { toast.error(t('error')); }
  };

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('backups_title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{items.length} entries</p>
        </div>
        {canRun && <Button onClick={run} data-testid="backups-trigger-button"><PlayCircle className="h-4 w-4 mr-2" />{t('backups_run_now')}</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-card border border-border">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
              <th className="px-4 py-3">When</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Size</th><th className="px-4 py-3">Trigger</th><th className="px-4 py-3">Location</th>
            </tr></thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`backup-row-${b.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{b.started_at ? formatDistanceToNow(new Date(b.started_at), { addSuffix: true }) : ''}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${b.status === 'success' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>{b.status}</span></td>
                  <td className="px-4 py-3 font-mono tabular-nums">{b.size_mb} MB</td>
                  <td className="px-4 py-3 text-xs text-[hsl(var(--muted-foreground))]">{b.trigger}</td>
                  <td className="px-4 py-3 font-mono text-xs truncate max-w-[280px]">{b.location}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">{t('no_data')}</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card className="p-5 bg-card border border-border" data-testid="backups-schedule-form">
          <h2 className="font-display text-lg font-semibold mb-3">{t('backups_schedule')}</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Enabled</Label>
              <Switch checked={schedule.enabled} onCheckedChange={(v) => setSchedule({ ...schedule, enabled: v })} disabled={!isAdmin} />
            </div>
            <div className="space-y-1">
              <Label>Cron</Label>
              <Input value={schedule.cron} onChange={(e) => setSchedule({ ...schedule, cron: e.target.value })} disabled={!isAdmin} className="font-mono" />
            </div>
            <div className="space-y-1">
              <Label>{t('backups_retention')}</Label>
              <Input type="number" value={schedule.retention_days} onChange={(e) => setSchedule({ ...schedule, retention_days: parseInt(e.target.value || 30, 10) })} disabled={!isAdmin} />
            </div>
            <Button className="w-full" onClick={saveSchedule} disabled={!isAdmin} data-testid="backups-schedule-save">{t('save')}</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
